import React, { useState, useEffect } from 'react'
import { Box, Typography, IconButton, Card, CardContent, Divider, Button, Modal, CardHeader, Grid2 } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import MainButton from '../../../components/MainButton'
import PDFWebViewComponent from '../../../components/PDFWebViewComponent'
import { useDocumentCreation } from '../../../contexts/DocumentCreationContext'
import AsynchronousActionButton from '../../../components/Buttons/AsynchronousActionButton'
import { BackIconButton } from '../../../components/Buttons/NavigationButtons'
import { getKeyAndValueFromObject } from '../../../utils/utils'
import { AsynchronousContinueButton } from '../../../components/Buttons'
import { generatePresignedUrl } from '../../../utils/utils'
import { useAuthInfo } from '@propelauth/react'

// Helper function to get file extension
const getFileExtension = (filenameOrUrl) => {
    if (!filenameOrUrl) return ''
    const filename = filenameOrUrl.split('/').pop()
    const parts = filename.split('.')
    return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

// Document Viewer Modal Component - Uses Apryse for all file types
const DocumentViewerModal = ({ open, onClose, fileUrl, fileName }) => {
    const fileExtension = getFileExtension(fileName)
    
    // List of supported file types that Apryse can convert to PDF
    const supportedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx', 'rtf', 'txt', 'xml', 'html', 'htm', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'tif']
    const isSupported = supportedExtensions.includes(fileExtension)
    
    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="document-viewer-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '90vw',
                    height: '90vh',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: 24,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: '#F9FAFB',
                    }}
                >
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                        {fileName || 'Document Viewer'}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            padding: '4px',
                            '&:hover': { backgroundColor: '#E5E7EB' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                </Box>

                {/* Document Viewer */}
                <Box sx={{ width: '100%', height: 'calc(100% - 73px)' }}>
                    {fileUrl && isSupported && (
                        <PDFWebViewComponent
                            initialFile={fileUrl}
                            filename={fileName}
                            hasLink={false}
                        />
                    )}
                    {fileUrl && !isSupported && (
                        <Box sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: 2
                        }}>
                            <Typography variant="h6" color="text.secondary">
                                File type not supported for preview
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Supported formats: PDF, DOC, DOCX, XLS, XLSX, CSV, PPT, PPTX, RTF, TXT, and common image formats
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    )
}

export default function DocumentReviewComponent({
    onBackClick,
    onConfirm,
    loading,
    success,
    setSuccess,
    setLoading,
}) {
    const navigate = useNavigate()
    const { document, resetDocumentCreation, documentSummary, allSections } = useDocumentCreation()
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false)
    const [selectedDocument, setSelectedDocument] = useState(null)
    const [selectedDocumentUrl, setSelectedDocumentUrl] = useState(null)
    const [selectedDocumentName, setSelectedDocumentName] = useState(null)

    const authInfo = useAuthInfo()

    useEffect(() => {
        const fetchDocumentUrl = async () => {
            console.log('fetchDocumentUrl')
            console.log('selectedDocument', selectedDocument)
            const orgId = authInfo.orgHelper.getOrgs()[0].orgId
            const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(orgId)

            if (selectedDocument?.url) {
                setSelectedDocumentUrl(selectedDocument.url)
                setSelectedDocumentName(selectedDocument.name)
            } else if (selectedDocument?.name) {
                if (selectedDocument?.name.includes(orgId)) {
                    const url = await generatePresignedUrl(selectedDocument.name, orgAccessToken)
                    setSelectedDocumentUrl(url)
                    setSelectedDocumentName(selectedDocument.name)
                } else {
                    const key = orgId + "/" + selectedDocument.name
                    const url = await generatePresignedUrl(key, orgAccessToken)
                    setSelectedDocumentUrl(url)
                    setSelectedDocumentName(selectedDocument.name)
                }
            } else {
                console.log('no document name or url')
            }
            setDocumentViewerOpen(true)
        }
        
        if (selectedDocument) {
            fetchDocumentUrl()
        }
    }, [selectedDocument])

    useEffect(() => {
        console.log('document', document)
        console.log('documentSummary', documentSummary)
    }, [])

    const handleViewFile = (file) => {
        console.log('file', file)
        setSelectedDocument(file)
    }

    const handleCloseViewer = () => {
        setDocumentViewerOpen(false)
        setSelectedDocument(null)
    }

    const handleConfirm = async (finalDocument) => {
        // Call the onConfirm callback which will handle document generation
        if (onConfirm) {
            await onConfirm(finalDocument)
        }
    }

    const formatFileName = (fileName) => {
        if (!fileName) return ''
        // Remove org ID and path prefixes
        return fileName.split('/').pop() || fileName
    }

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            padding: '40px',
            paddingTop: '120px', // Account for fixed stepper
            '@media (max-width: 768px)': {
                paddingTop: '100px',
            },
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Box sx={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999,
                width: 'clamp(600px, 60vw, 900px)',
                '@media (max-width: 768px)': {
                    width: 'calc(100vw - 32px)',
                    left: '16px',
                    transform: 'none',
                },
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 180px)',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
                '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Opera
                },
            }}>
                <Box sx={{ 
                    position: 'sticky', 
                    top: 0,
                    backgroundColor: 'white', 
                    zIndex: 999, 
                    background: '#F9FAFB',
                    paddingBottom: '24px'
                }}>
                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        { /* Back Button */ }
                        <BackIconButton onClick={onBackClick} />

                        { /* Title */ }
                        <Box>
                            <Typography 
                                variant="h4" 
                                sx={{ 
                                    fontSize: '24px',
                                    fontWeight: 600,
                                    color: '#111827',
                                    mb: 1
                                }}
                            >
                                Review document
                            </Typography>
                        </Box>
                    </Box>
                

                { /* Form Content */ }

                    {/* Template Details Card */}
                    <Card sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', borderRadius: '8px' }}>
                        <CardContent sx={{ padding: '16px' }}>
                            <Grid2 container spacing={3}>
                                <Grid2 size={4}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                                        <b>Name</b>
                                    </Typography>
                                </Grid2>
                                <Grid2 size={8}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#6B7280', mb: 0.5 }}>
                                        {document.name || 'Not specified'}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={4}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                                        <b>Template</b>
                                    </Typography>
                                </Grid2>
                                <Grid2 size={8}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#6B7280', mb: 0.5 }}>
                                        {document.templateType.template_name || 'Not specified'}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={4}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                                        <b>Type</b>
                                    </Typography>
                                </Grid2>
                                <Grid2 size={8}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#6B7280', mb: 0.5 }}>
                                        {document.templateType.detected_document_type.toUpperCase() || 'Not specified'}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={4}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                                        <b>Sections to be generated</b>
                                    </Typography>
                                </Grid2>
                                <Grid2 size={8}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#6B7280', mb: 0.5 }}>
                                        {document.selectedSections?.length === allSections?.length ? 
                                        "All Section in the Template" : 
                                        `Selected ${document.selectedSections.length} sections`}
                                    </Typography>
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>
                </Box>

                {/* Reference File Card */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: '736px', pb: '200px' }}>
                    {/* Reference File Card */}
                    <Box>
                        <Card sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', borderRadius: '8px' }}>
                            <CardHeader
                                title={
                                    <Typography sx={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: '#374151' }}>
                                        Source Files
                                    </Typography>
                                }
                                sx={{
                                    padding: '16px 16px 4px 16px',
                                }}
                            />
                            <CardContent sx={{ padding: '4px 16px 16px 16px' }}>
                                {document.referenceFiles && document.referenceFiles.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {document.referenceFiles.map((file, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    padding: '12px 16px',
                                                    backgroundColor: '#F9FAFB',
                                                    borderRadius: '6px',
                                                    border: '1px solid #E5E7EB',
                                                }}
                                            >   
                                                <InsertDriveFileIcon sx={{ fontSize: 20, color: '#6B7280' }} />
                                                <Typography sx={{ fontSize: '14px', color: '#374151', flex: 1 }}>
                                                    {formatFileName(file.name || file)}
                                                </Typography>
                                                <Button
                                                    onClick={() => handleViewFile(file)}
                                                    sx={{
                                                        px: 2,
                                                        py: 0.5,
                                                        backgroundColor: 'transparent',
                                                        color: '#4F46E5',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        textTransform: 'none',
                                                        minWidth: 'auto',
                                                        '&:hover': {
                                                            backgroundColor: '#F3F4F6',
                                                        },
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic' }}>
                                        No source files uploaded
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Box>

            {/* Navigation Buttons */}
            <AsynchronousContinueButton
                onClick={() => handleConfirm(document)}
                text="Confirm"
                backgroundColor="#4F46E5"
                fontColor="#FFFFFF"
                loading={loading}
                setLoading={setLoading}
                disabled={loading}
                sx={{
                    minHeight: '48px',
                }}
            />

            {/* Document Viewer Modal */}
            <DocumentViewerModal
                open={documentViewerOpen}
                onClose={handleCloseViewer}
                fileUrl={selectedDocumentUrl}
                fileName={formatFileName(selectedDocumentName)}
            />
        </Box>
    )
}
